/**
 * iter 370ea — Centralized helper for signing same-origin API URLs
 * with the user's session token so they work in contexts where the
 * browser can't carry an Authorization header (`<img src>`,
 * `<iframe src>`, `<a download>`, `window.open`, anchor links, etc.).
 *
 * Why this is needed
 * ==================
 * `AuthGateMiddleware` (backend/utils/middleware.py, shipped May 1)
 * fail-closes auth on every `/api/*` request. Our axios layer attaches
 * `Authorization: Bearer <session_token>` via the global request
 * interceptor — but browsers don't send headers for asset URLs. So
 * every `<img src="/api/photo-storage/<id>">` (Diego's broken-image
 * report), every printable PDF iframe, every `window.open()` of a
 * report URL, etc. silently 401s.
 *
 * The middleware ALREADY supports a `?session_token=` query-param
 * fallback for exactly this case (see middleware.py:686). This helper
 * appends that token to any same-origin `/api/*` URL.
 *
 * Safety
 * ======
 *   • Only same-origin (own backend) URLs are signed — never leaks
 *     the token to Cloudinary, Mapbox, Airtable, etc.
 *   • Idempotent — if the URL already carries `session_token`, returns
 *     it unchanged. Safe to call twice.
 *   • Try/catch wrap — malformed URLs pass through (no renderer crash).
 *
 * Naming
 * ======
 *   • `signApiUrl(url)` — the canonical name. Use this in new code.
 *   • `resolvePhotoUrl(url)` — legacy alias kept for the street-legal
 *     code path (and re-exported here so consumers don't have to know
 *     where it lives).
 */
import BACKEND_URL from "./apiConfig";


/**
 * Sign a backend URL with the current session token so it works as
 * a direct browser-load (img, iframe, anchor download).
 *
 * @param {string} url Either a relative `/api/...` path OR an absolute
 *                     URL. External hosts are returned unchanged.
 * @returns {string}   Same URL with `?session_token=…` appended when
 *                     the user is logged in AND the URL targets our
 *                     backend's `/api/*` namespace.
 */
export function signApiUrl(url) {
  if (!url) return url;
  // Promote relative paths to absolute against our backend so we can
  // parse them with `new URL(…)`.
  const absolute = url.startsWith("/") ? `${BACKEND_URL}${url}` : url;
  try {
    const parsed = new URL(absolute);
    const backendHost = new URL(BACKEND_URL || window.location.origin).host;
    // Only sign URLs hitting our own backend, and only API paths
    // (other paths like `/static/...` don't go through AuthGate).
    if (parsed.host !== backendHost) return absolute;
    if (!parsed.pathname.startsWith("/api/")) return absolute;
    if (parsed.searchParams.has("session_token")) return absolute;
    const token = typeof localStorage !== "undefined"
      ? localStorage.getItem("session_token")
      : null;
    if (!token) return absolute;
    parsed.searchParams.set("session_token", token);
    return parsed.toString();
  } catch {
    return absolute;
  }
}


/**
 * Legacy alias used by the Street Legal codepath. Kept identical so
 * existing imports don't break.
 */
export const resolvePhotoUrl = signApiUrl;
